
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { ShieldOff, Shield } from "lucide-react";

interface MediaSharingControlsProps {
  matchId: string;
  partnerName: string;
  onSharingStatusChange: () => void;
}

const MediaSharingControls = ({ matchId, partnerName, onSharingStatusChange }: MediaSharingControlsProps) => {
  const { user } = useAuth();
  const [isBlocked, setIsBlocked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);

  const parseMatchId = (matchId: string): string[] => {
    if (matchId.length < 73) return [];
    
    const firstUuidEnd = 36;
    const user1Id = matchId.substring(0, firstUuidEnd);
    const user2Id = matchId.substring(firstUuidEnd + 1);
    
    return [user1Id, user2Id];
  };

  useEffect(() => {
    checkSharingStatus();
  }, [matchId, user]);

  const checkSharingStatus = async () => {
    if (!user) return;

    try {
      setCheckingStatus(true);
      
      const userIds = parseMatchId(matchId);
      const otherUserId = userIds.find(id => id !== user.id);
      
      if (!otherUserId) return;

      const { data, error } = await supabase
        .from('media_sharing_blocks')
        .select('id')
        .eq('blocker_id', user.id)
        .eq('blocked_from_id', otherUserId)
        .eq('match_id', matchId)
        .maybeSingle();

      if (error) throw error;
      
      setIsBlocked(!!data);
    } catch (error: any) {
      console.error('Error checking sharing status:', error);
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleStopSharing = async () => {
    if (!user) return;

    try {
      setLoading(true);
      
      const userIds = parseMatchId(matchId);
      const otherUserId = userIds.find(id => id !== user.id);
      
      if (!otherUserId) return;

      const { error } = await supabase
        .from('media_sharing_blocks')
        .insert({
          blocker_id: user.id,
          blocked_from_id: otherUserId,
          match_id: matchId
        });

      if (error) throw error;

      setIsBlocked(true);
      onSharingStatusChange();
      
      toast({
        title: "Media Sharing Stopped",
        description: `You have stopped sharing your media with ${partnerName}`,
      });
    } catch (error: any) {
      console.error('Error stopping media sharing:', error);
      toast({
        title: "Error",
        description: "Failed to stop media sharing",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResumeSharing = async () => {
    if (!user) return;

    try {
      setLoading(true);
      
      const userIds = parseMatchId(matchId);
      const otherUserId = userIds.find(id => id !== user.id);
      
      if (!otherUserId) return;

      const { error } = await supabase
        .from('media_sharing_blocks')
        .delete()
        .eq('blocker_id', user.id)
        .eq('blocked_from_id', otherUserId)
        .eq('match_id', matchId);

      if (error) throw error;

      setIsBlocked(false);
      onSharingStatusChange();
      
      toast({
        title: "Media Sharing Resumed",
        description: `You have resumed sharing your media with ${partnerName}`,
      });
    } catch (error: any) {
      console.error('Error resuming media sharing:', error);
      toast({
        title: "Error",
        description: "Failed to resume media sharing",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (checkingStatus) {
    return null;
  }

  return (
    <div className="flex flex-col items-center space-y-2">
      {isBlocked ? (
        <div className="text-center">
          <div className="flex items-center justify-center text-orange-600 mb-2">
            <ShieldOff className="w-4 h-4 mr-1" />
            <span className="text-sm">Media sharing stopped</span>
          </div>
          <Button
            onClick={handleResumeSharing}
            disabled={loading}
            variant="outline"
            size="sm"
            className="text-green-600 border-green-600 hover:bg-green-50"
          >
            <Shield className="w-4 h-4 mr-2" />
            Resume Sharing
          </Button>
        </div>
      ) : (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="text-red-600 border-red-600 hover:bg-red-50"
              disabled={loading}
            >
              <ShieldOff className="w-4 h-4 mr-2" />
              Stop Sharing Media
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Stop Sharing Media?</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to stop sharing your media with {partnerName}? 
                This will hide all your albums and photos from them. You can resume sharing later if you change your mind.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleStopSharing}
                className="bg-red-600 hover:bg-red-700"
              >
                Stop Sharing
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
};

export default MediaSharingControls;
